import { useEffect, useState, useRef, useCallback } from 'react';
import EventAccordion from './components/EventAccordion';
import CafeMenu from './components/CafeMenu';

const SCHEMA_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'CafeOrCoffeeShop',
      '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/#cafe`,
      name: 'episode',
      url: import.meta.env.VITE_SITE_URL ?? 'https://example.com',
      description:
        '大阪・南森町のカフェepisode。オーガニック食材のモーニング・ランチ・コーヒー・グルテンフリースイーツを提供。ライブ・展示・ポップアップ・ワークショップに対応したイベントスペース・レンタルスペース。南森町駅徒歩3分、着席67席・立席100名対応。',
      image:
        'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/8c315cc5-2631-4358-98f7-07a190ead47c_HP_1st_01.jpg?v=7a1681ac1bf02d86d140836e1081dd62',
      telephone: '',
      sameAs: ['https://www.instagram.com/episode_t21'],
      address: {
        '@type': 'PostalAddress',
        streetAddress: '紅梅町2-3',
        addressLocality: '大阪市北区',
        addressRegion: '大阪府',
        postalCode: '530-0041',
        addressCountry: 'JP',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '34.698765',
        longitude: '135.512345',
      },
      servesCuisine: ['カフェ', 'オーガニック', 'グルテンフリー'],
      priceRange: '¥¥',
      hasMap: 'https://maps.google.com/maps?q=大阪府大阪市北区紅梅町2-3',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday'],
          opens: '08:00',
          closes: '17:00',
        },
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Saturday', 'Sunday'],
          opens: '11:30',
          closes: '18:00',
        },
      ],
      amenityFeature: [
        {
          '@type': 'LocationFeatureSpecification',
          name: 'イベントスペース',
          value: '着席67席、立席100名',
        },
        {
          '@type': 'LocationFeatureSpecification',
          name: 'レンタルスペース',
          value: 'ライブ、展示、ワークショップ対応',
        },
      ],
    },
    {
      '@type': 'WebSite',
      '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/#website`,
      url: import.meta.env.VITE_SITE_URL ?? 'https://example.com',
      name: 'episode',
      description: '大阪・南森町のカフェ・モーニング・ランチ・コーヒー・イベントスペース・レンタルスペース',
      inLanguage: 'ja',
    },
    {
      '@type': 'LocalBusiness',
      '@id': `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/#localbusiness`,
      name: 'episode',
      description:
        '大阪・南森町のカフェ。モーニング・ランチ・コーヒー・グルテンフリースイーツ。ライブ・展示・ポップアップ・ワークショップに対応したイベントスペース・レンタルスペース。南森町駅徒歩3分。',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '紅梅町2-3',
        addressLocality: '大阪市北区',
        addressRegion: '大阪府',
        postalCode: '530-0041',
        addressCountry: 'JP',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: '34.698765',
        longitude: '135.512345',
      },
      sameAs: ['https://www.instagram.com/episode_t21'],
    },
  ],
};



const HERO_SLIDES = [
  {
    image: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/8c315cc5-2631-4358-98f7-07a190ead47c_HP_1st_01.jpg?v=7a1681ac1bf02d86d140836e1081dd62',
    alt: 'episode cafe interior',
  },
  {
    image: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/78c5a5ee-9f13-4edc-b4a2-d4c5ff144620_HP_1st_02.jpg?v=df1044e5531b5960ca18c831b456b799',
    alt: 'episode cafe exterior',
  },
  {
    image: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/2ac2d8cb-7f29-4cf1-9384-a05cd0370279_HP_1st_03.jpg?v=c994c2cc0d833811438c36564c6f8de6',
    alt: 'episode live stage',
  },
];

// Animation hook
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

// Animated section wrapper
function AnimSection({ children, className = '', delay = 0, direction = 'up' }: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'left' | 'right' | 'fade';
}) {
  const { ref, inView } = useInView();
  const transforms: Record<string, string> = {
    up: 'translateY(40px)',
    left: 'translateX(-40px)',
    right: 'translateX(40px)',
    fade: 'translateY(0px)',
  };
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translate(0,0)' : transforms[direction],
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function HomePage() {
  const [scrollY, setScrollY] = useState(0);
  const [navbarBg, setNavbarBg] = useState(false);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-ld-home';
    script.textContent = JSON.stringify(SCHEMA_LD);
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById('schema-ld-home');
      if (existing) existing.remove();
    };
  }, []);
  const [heroSlide, setHeroSlide] = useState(0);
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isNearFooter, setIsNearFooter] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [modalImage, setModalImage] = useState<{ src: string; alt: string } | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
      setNavbarBg(currentScrollY > 100);
      setShowStickyNav(currentScrollY > 600);

      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollBottom = currentScrollY + windowHeight;
      const distanceFromBottom = documentHeight - scrollBottom;
      setIsNearFooter(distanceFromBottom < 200);

      const sections = ['home', 'cafe', 'events', 'space', 'rental', 'access'];
      const sectionElements = sections.map(id => ({
        id,
        element: document.getElementById(id === 'home' ? 'hero' : id)
      }));

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const section = sectionElements[i];
        if (section.element) {
          const rect = section.element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const heroTimer = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 5000);
    return () => clearInterval(heroTimer);
  }, []);

  const scrollToSection = useCallback((sectionId: string) => {
    const element = document.getElementById(sectionId === 'home' ? 'hero' : sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navbarBg ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-6 flex justify-between items-center">
          <img
            src="https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/51d60c404accc5d7ac3571284cfdc0c0.png"
            alt="episode"
            className="h-8 w-auto"
          />
          <div className="hidden md:flex gap-8 text-sm tracking-wider">
            {['About', 'Cafe', 'Events', 'Rental', 'Access'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className={`transition-colors duration-300 cursor-pointer hover:text-[#A790C4] relative group ${navbarBg ? 'text-gray-800' : 'text-white'}`}
              >
                {item}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#A790C4] transition-all duration-300 group-hover:w-full rounded-full"></span>
              </a>
            ))}
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5 cursor-pointer"
            aria-label="Toggle menu"
          >
            <span className={`w-6 h-0.5 transition-all duration-300 ${navbarBg ? 'bg-gray-800' : 'bg-white'} ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`w-6 h-0.5 transition-all duration-300 ${navbarBg ? 'bg-gray-800' : 'bg-white'} ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`w-6 h-0.5 transition-all duration-300 ${navbarBg ? 'bg-gray-800' : 'bg-white'} ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 md:hidden ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setMobileMenuOpen(false)}
      ></div>

      {/* Mobile Menu Slide-out */}
      <div className={`fixed top-0 right-0 h-full w-72 bg-white z-50 shadow-2xl transition-transform duration-300 md:hidden ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex justify-end p-6">
            <button onClick={() => setMobileMenuOpen(false)} className="w-10 h-10 flex items-center justify-center cursor-pointer" aria-label="Close menu">
              <i className="ri-close-line text-2xl text-gray-800"></i>
            </button>
          </div>
          <nav className="flex flex-col px-6 py-4 space-y-6">
            {[
              { id: 'home', label: 'Home' },
              { id: 'cafe', label: 'Cafe' },
              { id: 'atmosphere', label: 'Atmosphere' },
              { id: 'events', label: 'Events' },
              { id: 'space', label: 'Space' },
              { id: 'rental', label: 'Rental' },
              { id: 'access', label: 'Access' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`text-left text-lg tracking-wide transition-colors cursor-pointer ${activeSection === item.id ? 'text-[#A790C4] font-medium' : 'text-gray-800 hover:text-[#A790C4]'}`}
              >
                {item.label}
              </button>
            ))}
            <div className="pt-6 border-t border-gray-200">
              <a href="https://instagram.com/episode_t21" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-lg tracking-wide text-gray-800 hover:text-[#A790C4] transition-colors cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
                <i className="ri-instagram-line text-xl"></i>
                Instagram
              </a>
            </div>
          </nav>
        </div>
      </div>

      {/* Sticky Scroll Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm transition-all duration-500 ${showStickyNav ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}
        style={{ height: '60px' }}
      >
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-center">
          <div className="flex gap-8 text-sm tracking-wider">
            {[
              { id: 'home', label: 'Home' },
              { id: 'cafe', label: 'Cafe' },
              { id: 'events', label: 'Events' },
              { id: 'space', label: 'Space' },
              { id: 'rental', label: 'Rental' },
              { id: 'access', label: 'Access' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`transition-colors cursor-pointer hover:text-[#A790C4] whitespace-nowrap relative group ${activeSection === item.id ? 'text-[#A790C4] font-medium' : 'text-gray-800'}`}
              >
                {item.label}
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-[#A790C4] rounded-full transition-all duration-300 ${activeSection === item.id ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Fixed Instagram CTA Button — icon only */}
      <a
        href="https://instagram.com/episode_t21"
        target="_blank"
        rel="noopener noreferrer"
        className={`fixed z-50 bg-[#A790C4] text-white rounded-full shadow-lg hover:shadow-xl hover:bg-[#386663] transition-all duration-300 cursor-pointer flex items-center justify-center ${isNearFooter ? 'opacity-0 pointer-events-none scale-75' : 'opacity-100 scale-100'}`}
        style={{ right: '24px', bottom: '24px', width: '56px', height: '56px' }}
        aria-label="Instagram"
      >
        <i className="ri-instagram-line text-2xl"></i>
      </a>

      {/* Hero Section */}
      <section id="hero" className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          {HERO_SLIDES.map((slide, index) => (
            <div
              key={index}
              className="absolute inset-0 transition-opacity duration-1500"
              style={{ opacity: heroSlide === index ? 1 : 0, transitionDuration: '1500ms' }}
            >
              <img
                src={slide.image}
                alt={slide.alt}
                className="w-full h-full object-cover object-center"
                style={{
                  transform: `scale(${1 + scrollY * 0.0002})`,
                  transition: 'transform 0.1s linear',
                }}
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        </div>
        <div className="relative h-full flex flex-col items-center justify-center text-white px-6">
          <img
            src="https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/ce784064396c70f7faf6f634edca28a4.png"
            alt="episode"
            className="mb-6"
            style={{
              width: '420px',
              maxWidth: '80vw',
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: 'opacity 1s ease 0.2s, transform 1s ease 0.2s',
            }}
          />
          <p
            className="text-sm md:text-base tracking-widest mb-8 font-light"
            style={{
              letterSpacing: '0.3em',
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 1s ease 0.5s, transform 1s ease 0.5s',
            }}
          >
            cafe / music / art
          </p>
          <p
            className="text-lg md:text-xl font-light tracking-wide"
            style={{
              fontFamily: 'serif',
              opacity: heroVisible ? 1 : 0,
              transform: heroVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 1s ease 0.8s, transform 1s ease 0.8s',
            }}
          >
            何気ない日常に少しの高揚感を
          </p>
        </div>
        {/* Slide indicators */}
        <div
          className="absolute bottom-20 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10"
          style={{ opacity: heroVisible ? 1 : 0, transition: 'opacity 1s ease 1.2s' }}
        >
          {HERO_SLIDES.map((_, index) => (
            <button
              key={index}
              onClick={() => setHeroSlide(index)}
              className="transition-all duration-300 rounded-full cursor-pointer"
              style={{
                width: heroSlide === index ? '24px' : '8px',
                height: '8px',
                backgroundColor: heroSlide === index ? '#A790C4' : 'rgba(255,255,255,0.5)',
              }}
            />
          ))}
        </div>
        <div
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce"
          style={{ opacity: heroVisible ? 1 : 0, transition: 'opacity 1s ease 1.2s' }}
        >
          <i className="ri-arrow-down-line text-white text-2xl"></i>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <AnimSection>
            <div className="flex items-center gap-3 mb-12">
              <div className="w-1 h-8 bg-[#A790C4] rounded-full"></div>
              <h2 className="text-xs tracking-widest text-[#000000] font-light" style={{ letterSpacing: '0.3em' }}>ABOUT EPISODE</h2>
            </div>
          </AnimSection>
          <div className="space-y-8">
            {[
              '大阪・南森町にあるカフェ「episode」。\nコーヒーやフードを楽しめる空間でありながら、音楽・アート・展示・ポップアップなど、さまざまな表現が交差する場所です。',
              '何気ない日常の中で、少し気分が上がる瞬間。\nここで過ごす時間が、誰かの小さなエピソードになることを願っています。',
              '南森町駅から徒歩約3分の場所にあり、ゆっくりと過ごせるカフェとして営業しています。\n展示、ライブ、トークイベント、ポップアップショップなどのイベントスペースとしても利用されています。',
            ].map((text, i) => (
              <AnimSection key={i} delay={i * 120}>
                <p className="text-base md:text-lg leading-loose text-gray-800" style={{ fontFamily: 'serif', lineHeight: '2' }}>
                  {text.split('\n').map((line, j) => (
                    <span key={j}>{line}{j < text.split('\n').length - 1 && <br />}</span>
                  ))}
                </p>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* About → Cafe Divider Photo */}
      <AnimSection direction="fade">
        <div className="w-full overflow-hidden" style={{ height: '480px' }}>
          <img
            src="https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/7ce7c7b7e17a7360d3afec14a4915100.jpeg"
            alt="episode cafe atmosphere"
            className="w-full h-full object-cover object-center"
          />
        </div>
      </AnimSection>

      {/* Cafe Section */}
      <section id="cafe" className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="max-w-2xl mx-auto mb-16">
            <AnimSection>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#A790C4]">
                  <i className="ri-cup-line text-lg text-white"></i>
                </div>
                <h2 className="text-xs tracking-widest text-[#000000] font-light" style={{ letterSpacing: '0.3em' }}>FOOD &amp; DRINKS</h2>
              </div>
            </AnimSection>
            <AnimSection delay={100}>
              <p className="text-base leading-relaxed text-gray-800" style={{ fontFamily: 'serif', lineHeight: '1.9' }}>
                episodeでは、オーガニックの食材や調味料にこだわり、身体にやさしい食事とドリンクをご提供しています。<br />
                グルテンフリーのスイーツから、シンプルに仕上げた料理まで。心と身体に寄り添うメニューをそろえています。<br />
                ゆったりとした空間の中で、ランチやコーヒー、穏やかなひとときをお楽しみください。
              </p>
            </AnimSection>
          </div>
          <AnimSection delay={150}>
            <CafeMenu />
          </AnimSection>
        </div>
      </section>

      {/* Atmosphere Section */}
      <section id="atmosphere" className="py-24 bg-[#E6E7E8]">
        <div className="py-4 px-6 max-w-xl mx-auto text-center mb-16">
          <AnimSection>
            <div className="flex items-center justify-center gap-3 mb-10">
              <div className="w-12 h-0.5 bg-[#A790C4] rounded-full"></div>
              <h2 className="text-xs tracking-widest text-[#000000] font-light" style={{ letterSpacing: '0.35em' }}>ATMOSPHERE</h2>
              <div className="w-12 h-0.5 bg-[#A790C4] rounded-full"></div>
            </div>
          </AnimSection>
          <AnimSection delay={100}><p className="text-base text-gray-700 leading-loose mb-6" style={{ fontFamily: 'serif', lineHeight: '2.2' }}>ゆっくりと流れる時間の中で、心をほどく場所。</p></AnimSection>
          <AnimSection delay={180}><p className="text-base text-gray-700 leading-loose mb-6" style={{ fontFamily: 'serif', lineHeight: '2.2' }}>大きな窓と落ち着いた室内を背景に — コーヒー、会話、音楽、小さな集まり。</p></AnimSection>
          <AnimSection delay={260}><p className="text-base text-gray-700 leading-loose" style={{ fontFamily: 'serif', lineHeight: '2.2' }}>episodeは、日常の小さなエピソードが生まれる場所です。</p></AnimSection>
        </div>

        {/* Staggered mosaic grid */}
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-12 grid-rows-2 gap-3" style={{ height: '560px' }}>
            <AnimSection className="col-span-3 row-span-2 overflow-hidden rounded-2xl" direction="left">
              <img src="https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/9b642a7659c960aaf99d3b8a25e512a8.png" alt="light shadow pattern" className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700" />
            </AnimSection>
            <AnimSection className="col-span-5 row-span-1 overflow-hidden rounded-2xl" delay={80}>
              <img src="https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/8fdff1e450f4917336691517a12528c2.png" alt="wood grain texture" className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700" />
            </AnimSection>
            <AnimSection className="col-span-4 row-span-1 overflow-hidden rounded-2xl" direction="right" delay={120}>
              <img src="https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/d6a94ccad55d95229dd4300b303dd092.png" alt="window condensation bokeh" className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700" />
            </AnimSection>
            <AnimSection className="col-span-3 row-span-1 overflow-hidden rounded-2xl" delay={160}>
              <img src="https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/b63ac6ea735ec4c064b10729c671ad88.png" alt="dried pampas grass detail" className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700" />
            </AnimSection>
            <AnimSection className="col-span-6 row-span-1 overflow-hidden rounded-2xl" direction="right" delay={200}>
              <img src="https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/85a6f6930bb52de5b90524147f05fb8a.png" alt="abstract light reflection" className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700" />
            </AnimSection>
          </div>
        </div>

        <AnimSection delay={100}>
          <div className="text-center mt-14 px-6">
            <p className="text-sm text-gray-500 tracking-widest font-light" style={{ fontFamily: 'serif', letterSpacing: '0.25em' }}>
              この空間には、穏やかな高揚感がある。
            </p>
          </div>
        </AnimSection>
      </section>

      {/* この場所でできること — bridge section leading into WHAT HAPPENS HERE */}
      <section className="py-28 px-6 bg-[#E6E7E8]">
        <div className="max-w-3xl mx-auto text-center">
          <AnimSection>
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-16 h-0.5 bg-[#A790C4] rounded-full"></div>
              <p className="text-xs tracking-widest text-gray-500 font-light" style={{ letterSpacing: '0.35em' }}>WHAT YOU CAN DO HERE</p>
              <div className="w-16 h-0.5 bg-[#A790C4] rounded-full"></div>
            </div>
          </AnimSection>
          <AnimSection delay={80}>
            <h2 className="text-3xl md:text-4xl text-gray-900 font-light mb-12 tracking-wide" style={{ fontFamily: 'serif' }}>
              この場所でできること
            </h2>
          </AnimSection>
          <AnimSection delay={140}>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6" style={{ fontFamily: 'serif', lineHeight: '2' }}>
              episodeはカフェでありながら、<br />さまざまなイベントに対応できる<br />柔軟な空間です。
            </p>
          </AnimSection>
          <AnimSection delay={200}>
            <p className="text-base text-gray-500 leading-loose mb-14" style={{ fontFamily: 'serif', lineHeight: '2' }}>
              日常のコーヒータイムから、音楽・アート・カルチャーの発信の場まで。<br />
              この空間は、使う人の想像力で形を変えます。
            </p>
          </AnimSection>
          {/* Inline event type tags */}
          <AnimSection delay={260}>
            <div className="flex flex-col gap-4 mb-14">
              {/* 上段 3つ */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: 'ri-music-2-line', label: 'ライブイベント', color: '#A790C4' },
                  { icon: 'ri-gallery-line', label: 'アート展示', color: '#386663' },
                  { icon: 'ri-store-2-line', label: 'ポップアップ', color: '#C696A8' },
                ].map((tag, i) => (
                  <div
                    key={i}
                    className="flex flex-col items-center gap-3 px-4 py-8 rounded-2xl shadow-sm bg-white border-2 border-transparent hover:border-[#A790C4] transition-all duration-300 cursor-pointer hover:shadow-md hover:-translate-y-1"
                  >
                    <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ backgroundColor: tag.color }}>
                      <i className={`${tag.icon} text-2xl text-white`}></i>
                    </div>
                    <span className="text-base text-gray-800 tracking-wide font-light" style={{ fontFamily: 'serif' }}>
                      {tag.label}
                    </span>
                  </div>
                ))}
              </div>
              {/* 下段 3つ */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: 'ri-tools-line', label: 'ワークショップ', color: '#A790C4' },
                  { icon: 'ri-chat-3-line', label: 'トークイベント', color: '#386663' },
                  { icon: 'ri-camera-line', label: '撮影利用', color: '#C696A8' },
                ].map((tag, i) => (
                  <div
                    key={i}
                    className="flex flex-col items-center gap-3 px-4 py-8 rounded-2xl shadow-sm bg-white border-2 border-transparent hover:border-[#A790C4] transition-all duration-300 cursor-pointer hover:shadow-md hover:-translate-y-1"
                  >
                    <div className="w-14 h-14 flex items-center justify-center rounded-full" style={{ backgroundColor: tag.color }}>
                      <i className={`${tag.icon} text-2xl text-white`}></i>
                    </div>
                    <span className="text-base text-gray-800 tracking-wide font-light" style={{ fontFamily: 'serif' }}>
                      {tag.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </AnimSection>
        </div>
      </section>



      {/* Events Section */}
      <section id="events" className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <AnimSection>
            <div className="text-center mb-8">
              <h2 className="text-3xl text-[#000000] mb-3 font-light tracking-wide">Events</h2>
              <div className="w-20 h-1 bg-[#A790C4] rounded-full mx-auto"></div>
            </div>
          </AnimSection>
          <AnimSection delay={100}>
            <p className="text-base text-gray-800 text-center mb-12" style={{ fontFamily: 'serif', lineHeight: '1.9' }}>
              episodeでは以下のようなイベントが開催されています。
            </p>
          </AnimSection>
          <AnimSection delay={150}>
            <EventAccordion />
          </AnimSection>

          <AnimSection delay={200}>
            <div className="mt-16 text-center bg-gradient-to-br from-[#E6E7E8] to-[#F5F5F5] rounded-3xl py-10 px-6 border-2 border-[#A790C4]/20">
              <h3 className="text-xl text-[#000000] mb-3 font-light tracking-wide" style={{ fontFamily: 'serif' }}>Past Events</h3>
              <p className="text-sm text-gray-800 mb-6" style={{ fontFamily: 'serif', lineHeight: '1.8' }}>
                過去のイベントや展示の様子は<br />Instagramのハイライトでご覧いただけます。
              </p>
              <a href="https://instagram.com/episode_t21" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-[#A790C4] border-2 border-[#A790C4] px-10 py-3.5 rounded-full text-sm tracking-wider hover:bg-[#A790C4] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg">
                <i className="ri-instagram-line text-base"></i>
                View Past Events on Instagram
              </a>
            </div>
          </AnimSection>

          <AnimSection delay={250}>
            <div className="text-center mt-12 bg-gradient-to-br from-[#A790C4]/10 to-[#C696A8]/10 rounded-3xl py-10 px-6 border-2 border-[#A790C4]/20">
              <p className="text-sm text-gray-800 mb-2" style={{ fontFamily: 'serif' }}>最新イベント情報はInstagramで更新しています</p>
              <p className="text-sm text-gray-800 mb-6" style={{ fontFamily: 'serif' }}>フォローして最新情報をチェック</p>
              <a href="https://instagram.com/episode_t21" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#A790C4] text-white px-10 py-3.5 rounded-full text-sm tracking-wider hover:bg-[#386663] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg">
                <i className="ri-instagram-line text-base"></i>
                Follow us @episode_t21
              </a>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* Space Rental Section */}
      <section id="rental" className="py-24 px-6 bg-[#E6E7E8]">
        <div className="max-w-5xl mx-auto">
          <AnimSection>
            <div className="flex items-center gap-3 mb-12">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#C696A8]">
                <i className="ri-key-2-line text-lg text-white"></i>
              </div>
              <h2 className="text-xs tracking-widest text-[#000000] font-light text-center" style={{ letterSpacing: '0.3em' }}>SPACE RENTAL</h2>
            </div>
          </AnimSection>
          <AnimSection delay={100}>
            <p className="text-base text-gray-800 text-center mb-12" style={{ fontFamily: 'serif', lineHeight: '1.9' }}>
              episodeのカフェ空間は、イベント、展示、ライブ音楽、ポップアップ、ワークショップ、撮影などにレンタルできます。
            </p>
          </AnimSection>

          <div className="space-y-8 mb-14">
            {/* Full Space Rental */}
            <AnimSection delay={120} direction="left">
              <div className="bg-white rounded-3xl overflow-hidden border-2 border-[#A790C4]/30 hover:border-[#A790C4] transition-colors duration-300 hover:shadow-lg">
                <div className="flex flex-col md:flex-row">
                  {/* Photo — replace src with HP_1st_02 when available */}
                  <div
                    className="relative overflow-hidden cursor-pointer group flex-shrink-0 w-full md:w-[340px] h-[240px] md:h-auto"
                    onClick={() => setModalImage({
                      src: 'https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/78c5a5ee-9f13-4edc-b4a2-d4c5ff144620_HP_1st_02.jpg?v=df1044e5531b5960ca18c831b456b799',
                      alt: 'Full Space Rental'
                    })}
                  >
                    <img
                      src="https://storage.readdy-site.link/project_files/8e67da46-5e1d-4785-abe7-63d36243e4bc/78c5a5ee-9f13-4edc-b4a2-d4c5ff144620_HP_1st_02.jpg?v=df1044e5531b5960ca18c831b456b799"
                      alt="Full Space Rental"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <i className="ri-zoom-in-line text-[#A790C4] text-lg"></i>
                      </div>
                    </div>
                  </div>
                  {/* Text */}
                  <div className="flex-1 p-8 flex flex-col justify-center">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg text-[#000000] font-light tracking-wide" style={{ fontFamily: 'serif' }}>Full Space Rental</h3>
                      <span className="text-xs text-white tracking-wider px-3 py-1 rounded-full whitespace-nowrap ml-3" style={{ backgroundColor: '#A790C4' }}>Full</span>
                    </div>
                    <div className="space-y-3 mb-4">
                      {[
                        { text: '着席最大 67席', color: '#A790C4' },
                        { text: '立席最大 約100名', color: '#386663' },
                        { text: 'レイアウト変更可能', color: '#C696A8' },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }}></div>
                          <p className="text-sm text-gray-800" style={{ fontFamily: 'serif' }}>{item.text}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed" style={{ fontFamily: 'serif', lineHeight: '1.8' }}>
                      ライブ、展示、ポップアップなど大規模なイベントに対応
                    </p>
                  </div>
                </div>
              </div>
            </AnimSection>

            {/* Half Space Rental */}
            <AnimSection delay={200} direction="right">
              <div className="bg-white rounded-3xl overflow-hidden border-2 border-[#386663]/30 hover:border-[#386663] transition-colors duration-300 hover:shadow-lg">
                <div className="flex flex-col md:flex-row">
                  {/* Photo */}
                  <div
                    className="relative overflow-hidden cursor-pointer group flex-shrink-0 w-full md:w-[340px] h-[240px] md:h-auto"
                    onClick={() => setModalImage({
                      src: 'https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/8f7ce0449400d09abc6b63371758daca.jpeg',
                      alt: 'Half Space Rental'
                    })}
                  >
                    <img
                      src="https://static.readdy.ai/image/faab8405af96b700637a376baf26c6c6/8f7ce0449400d09abc6b63371758daca.jpeg"
                      alt="Half Space Rental"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <i className="ri-zoom-in-line text-[#386663] text-lg"></i>
                      </div>
                    </div>
                  </div>
                  {/* Text */}
                  <div className="flex-1 p-8 flex flex-col justify-center">
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-lg text-[#000000] font-light tracking-wide" style={{ fontFamily: 'serif' }}>Half Space Rental</h3>
                      <span className="text-xs text-white tracking-wider px-3 py-1 rounded-full whitespace-nowrap ml-3" style={{ backgroundColor: '#386663' }}>Half</span>
                    </div>
                    <div className="space-y-3 mb-4">
                      {[
                        { text: '16〜22席', color: '#386663' },
                        { text: 'レイアウト変更可能', color: '#A790C4' },
                        { text: '少人数イベントに最適', color: '#C696A8' },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }}></div>
                          <p className="text-sm text-gray-800" style={{ fontFamily: 'serif' }}>{item.text}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 leading-relaxed" style={{ fontFamily: 'serif', lineHeight: '1.8' }}>
                      ワークショップやセミナーなどの小規模イベントに適しています
                    </p>
                  </div>
                </div>
              </div>
            </AnimSection>
          </div>

          <AnimSection delay={280}>
            <div className="text-center bg-gradient-to-br from-[#A790C4]/10 to-[#C696A8]/10 rounded-3xl py-10 px-6 border-2 border-[#A790C4]/20">
              <h3 className="text-lg text-[#000000] mb-3 font-light tracking-wide" style={{ fontFamily: 'serif' }}>Rental Inquiry</h3>
              <p className="text-sm text-gray-800 mb-2" style={{ fontFamily: 'serif' }}>イベント開催やスペースレンタルのご相談は</p>
              <p className="text-sm text-gray-800 mb-6" style={{ fontFamily: 'serif' }}>Instagram DMにて受け付けています</p>
              <a href="https://instagram.com/episode_t21" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#A790C4] text-white px-10 py-3.5 rounded-full text-sm tracking-wider hover:bg-[#E6E7E8] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg">
                <i className="ri-instagram-line text-base"></i>
                Contact via Instagram
              </a>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* Photo Modal */}
      {modalImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setModalImage(null)}
        >
          <div
            className="relative max-w-4xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'fadeIn 0.25s ease' }}
          >
            <button
              onClick={() => setModalImage(null)}
              className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 transition-all cursor-pointer"
              aria-label="Close"
            >
              <i className="ri-close-line text-white text-xl"></i>
            </button>
            <img
              src={modalImage.src}
              alt={modalImage.alt}
              className="w-full rounded-2xl shadow-2xl"
              style={{ maxHeight: '80vh', objectFit: 'contain' }}
            />
            <p className="text-center text-white/70 text-sm mt-4 tracking-wider" style={{ fontFamily: 'serif' }}>{modalImage.alt}</p>
          </div>
        </div>
      )}

      {/* Equipment Section */}
      <section className="py-24 px-6 bg-[#E6E7E8]">
        <div className="max-w-3xl mx-auto">
          <AnimSection>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#386663]">
                <i className="ri-mic-line text-lg text-white"></i>
              </div>
              <h2 className="text-xs tracking-widest text-[#000000] font-light text-center" style={{ letterSpacing: '0.3em' }}>LIVE & EVENT EQUIPMENT</h2>
            </div>
          </AnimSection>
          <AnimSection delay={100}>
            <p className="text-base text-gray-800 text-center mb-12" style={{ fontFamily: 'serif', lineHeight: '1.9' }}>
              episodeは小規模なライブパフォーマンスやトークイベントに対応した設備を備えています。
            </p>
          </AnimSection>
          <AnimSection delay={150}>
            <div className="bg-white rounded-3xl p-10 max-w-lg mx-auto mb-8 border-2 border-[#A790C4]/20">
              <div className="space-y-6">
                {[
                  { name: 'ステージピアノ', detail: 'Roland RD‑2000EX', color: '#A790C4' },
                  { name: 'マイク', detail: '', color: '#386663' },
                  { name: 'デジタルミキサー', detail: '', color: '#C696A8' },
                  { name: 'スピーカー', detail: '', color: '#A790C4' },
                  { name: 'プロジェクター', detail: '', color: '#386663' }
                ].map((equipment, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-2 h-2 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: equipment.color }}></div>
                    <div>
                      <p className="text-base text-gray-800" style={{ fontFamily: 'serif' }}>{equipment.name}</p>
                      {equipment.detail && <p className="text-sm text-gray-600 mt-1" style={{ fontFamily: 'serif' }}>({equipment.detail})</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </AnimSection>
          <AnimSection delay={200}>
            <div className="flex items-center justify-center max-w-lg mx-auto">
              <a href="/equipment-list.pdf" download="episode_equipment_and_floorplan.pdf" className="flex items-center gap-2 bg-[#A790C4] text-white px-10 py-3.5 rounded-full text-sm tracking-wider hover:bg-[#386663] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg justify-center">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-file-list-3-line text-base"></i></div>
                機材リスト &amp; 図面 PDF
              </a>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* About episode (SEO Section) */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-6 text-base text-gray-800 leading-relaxed" style={{ fontFamily: 'serif', lineHeight: '1.9' }}>
            {[
              <><strong>episode</strong>は大阪・南森町にあるカフェです。</>,
              <>コーヒーやランチを楽しめるカフェとして営業しながら、<br />ライブイベント、展示、ポップアップショップ、ワークショップなどに利用できるイベントスペースとしても使われています。</>,
              <>南森町駅・大阪天満宮駅から徒歩約3分の立地にあり、<br />小規模ライブや展示、トークイベントなどに適した落ち着いた空間です。</>,
              <>カフェとして日常的に利用できる場所でありながら、<br />音楽・アート・カルチャーが交差する場所として、さまざまなイベントが開催されています。</>,
            ].map((text, i) => (
              <AnimSection key={i} delay={i * 100}>
                <p>{text}</p>
              </AnimSection>
            ))}
          </div>
        </div>
      </section>

      {/* Access Section */}
      <section id="access" className="py-24 px-6 bg-[#E6E7E8]">
        <div className="max-w-5xl mx-auto">
          <AnimSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl text-[#000000] mb-3 font-light tracking-wide" style={{ fontFamily: 'serif' }}>Access</h2>
              <div className="w-20 h-1 bg-[#A790C4] rounded-full mx-auto"></div>
            </div>
          </AnimSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-10">
            {/* Opening Hours Card */}
            <AnimSection delay={80} direction="left">
              <div className="bg-white rounded-3xl p-8 h-full border-2 border-[#A790C4]/20 flex flex-col">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#A790C4]">
                    <i className="ri-time-line text-lg text-white"></i>
                  </div>
                  <h3 className="text-xs tracking-widest text-[#000000] font-light" style={{ letterSpacing: '0.3em' }}>OPENING HOURS</h3>
                </div>
                <div className="mb-6 space-y-3">
                  <div>
                    <p className="text-xs text-gray-400 tracking-widest mb-1" style={{ fontFamily: 'serif', letterSpacing: '0.18em' }}>Morning</p>
                    <p className="text-xl text-[#000000] font-light tracking-wide" style={{ fontFamily: 'serif' }}>08:00 – 11:00</p>
                    <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: 'serif' }}>L.O. 10:45</p>
                  </div>
                  <div className="w-8 h-0.5 bg-[#A790C4]/30 rounded-full"></div>
                  <div>
                    <p className="text-xs text-gray-400 tracking-widest mb-1" style={{ fontFamily: 'serif', letterSpacing: '0.18em' }}>Weekdays</p>
                    <p className="text-xl text-[#000000] font-light tracking-wide" style={{ fontFamily: 'serif' }}>08:00 – 17:00</p>
                    <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: 'serif' }}>L.O. 16:30 &nbsp;·&nbsp; Lunch 11:00 – 15:00</p>
                  </div>
                  <div className="w-8 h-0.5 bg-[#A790C4]/30 rounded-full"></div>
                  <div>
                    <p className="text-xs text-gray-400 tracking-widest mb-1" style={{ fontFamily: 'serif', letterSpacing: '0.18em' }}>Weekends &amp; Holidays</p>
                    <p className="text-xl text-[#000000] font-light tracking-wide" style={{ fontFamily: 'serif' }}>11:30 – 18:00</p>
                    <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: 'serif' }}>L.O. 17:30 &nbsp;·&nbsp; Lunch 11:30 – 15:00</p>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <span className="w-2 h-2 rounded-full bg-[#A790C4] flex-shrink-0 inline-block"></span>
                    <p className="text-sm text-[#A790C4] tracking-wider" style={{ fontFamily: 'serif' }}>Closed: Fridays &amp; the day after public holidays</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-4 leading-relaxed" style={{ fontFamily: 'serif', lineHeight: '1.8' }}>
                    最新のスケジュールはInstagramをご確認ください。
                  </p>
                  <a
                    href="https://instagram.com/episode_t21"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#A790C4] text-white px-6 py-3 rounded-full text-sm tracking-wider hover:bg-[#E6E7E8] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-md hover:shadow-lg"
                    style={{ fontFamily: 'serif' }}
                  >
                    <i className="ri-instagram-line text-base"></i>
                    Follow us @episode_t21
                  </a>
                </div>
              </div>
            </AnimSection>

            {/* Location Card */}
            <AnimSection delay={120} direction="right">
              <div className="bg-white rounded-3xl p-8 h-full border-2 border-[#386663]/20 flex flex-col">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#386663]">
                    <i className="ri-map-pin-line text-lg text-white"></i>
                  </div>
                  <h3 className="text-xs tracking-widest text-[#000000] font-light" style={{ letterSpacing: '0.3em' }}>LOCATION</h3>
                </div>
                <h4 className="text-xl text-gray-800 font-semibold mb-4" style={{ fontFamily: 'serif' }}>episode</h4>
                <p className="text-base text-gray-800 mb-1" style={{ fontFamily: 'serif' }}>大阪府大阪市北区紅梅町2-3</p>
                <p className="text-sm text-gray-500 mb-4" style={{ fontFamily: 'serif' }}>2-3 Kobai-cho, Kita-ku, Osaka</p>
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-800 flex items-center gap-2" style={{ fontFamily: 'serif' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#A790C4] inline-block flex-shrink-0"></span>
                    南森町駅 徒歩3分
                  </p>
                  <p className="text-sm text-gray-800 flex items-center gap-2" style={{ fontFamily: 'serif' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#386663] inline-block flex-shrink-0"></span>
                    大阪天満宮駅 徒歩3分
                  </p>
                  <p className="text-sm text-gray-800 flex items-center gap-2" style={{ fontFamily: 'serif' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C696A8] inline-block flex-shrink-0"></span>
                    梅田エリアからもアクセス便利
                  </p>
                </div>
                <p className="text-sm text-gray-600 mt-auto leading-relaxed" style={{ fontFamily: 'serif', lineHeight: '1.9' }}>
                  コーヒーやランチを楽しめるカフェとして営業しながら、展示・ライブ・ポップアップなどのイベントスペースとしても利用されています。
                </p>
              </div>
            </AnimSection>
          </div>

          {/* Map */}
          <AnimSection delay={180}>
            <div className="rounded-3xl overflow-hidden shadow-md max-w-4xl mx-auto">
              <iframe
                src="https://maps.google.com/maps?q=大阪府大阪市北区紅梅町2-3&output=embed&hl=ja"
                width="100%"
                height="320"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="episode location map"
              ></iframe>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-[#A790C4] to-[#386663]">
        <AnimSection>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl text-white mb-10 font-light tracking-wide">Contact</h2>
            <p className="text-base text-white mb-2" style={{ fontFamily: 'serif' }}>イベントやレンタルのご相談は</p>
            <p className="text-base text-white mb-8" style={{ fontFamily: 'serif' }}>Instagram DMにて承っています</p>
            <div className="flex items-center justify-center gap-4">
              <a
                href="https://instagram.com/episode_t21"
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/35 transition-all duration-300 cursor-pointer flex-shrink-0"
                aria-label="Instagram"
              >
                <i className="ri-instagram-line text-2xl text-white"></i>
              </a>
              <a
                href="https://instagram.com/episode_t21"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-[#A790C4] px-8 py-3.5 rounded-full text-sm tracking-wider hover:bg-[#E6E7E8] transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
              >
                @episode_t21 をフォロー
              </a>
            </div>
          </div>
        </AnimSection>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-10">
        <div className="text-center">
          <p className="text-xs text-gray-600 font-light tracking-wide">©︎episode All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}
